import type { inferRouterOutputs } from "@trpc/server"
import type { AppRouter } from "@/server/api/root"
import type { FlowConnectorLane } from "@/components/desktop/ui/FlowConnectors"
import {
    FLOW_MODE_KEYS,
    type FlowModeKey,
} from "@/components/desktop/ui/ModeIcon"

export type WarehouseFlowView =
    inferRouterOutputs<AppRouter>["freightDashboard"]["warehouseFlow"]
export type WarehouseFlowCounts = WarehouseFlowView["totals"]
export type WarehouseFlowSite = WarehouseFlowView["sites"][number]
export type WarehouseFlowSiteCounts =
    WarehouseFlowView["others"]["bySite"][number]

const OPEN_STAGES = ["new", "booked", "inTransit", "onHold"] as const
const ALL_STAGES = [...OPEN_STAGES, "delivered"] as const

export const shouldFetchWarehouseFlow = ({
    isCovered,
    isHidden,
}: {
    isCovered: boolean
    isHidden: boolean
}): boolean => !isCovered && !isHidden

export const openOf = (counts: WarehouseFlowCounts): number =>
    OPEN_STAGES.reduce((sum, stage) => sum + counts.stages[stage], 0)

export const behindOf = (counts: WarehouseFlowCounts): number =>
    OPEN_STAGES.reduce((sum, stage) => sum + counts.behind[stage], 0)

export const totalOf = (counts: WarehouseFlowCounts): number =>
    ALL_STAGES.reduce((sum, stage) => sum + counts.stages[stage], 0)

export const sumSiteCounts = (
    bySite: Array<{ counts: WarehouseFlowCounts }>
): WarehouseFlowCounts =>
    bySite.reduce<WarehouseFlowCounts>(
        (sum, { counts }) => {
            ALL_STAGES.forEach(stage => {
                sum.stages[stage] += counts.stages[stage]
                sum.behind[stage] += counts.behind[stage]
            })
            return sum
        },
        {
            stages: {
                new: 0,
                booked: 0,
                inTransit: 0,
                onHold: 0,
                delivered: 0,
            },
            behind: {
                new: 0,
                booked: 0,
                inTransit: 0,
                onHold: 0,
                delivered: 0,
            },
        }
    )

export const mergeSiteCounts = (
    lists: WarehouseFlowSiteCounts[][]
): WarehouseFlowSiteCounts[] => {
    const bySiteId = new Map<string, WarehouseFlowSiteCounts[]>()
    lists.flat().forEach(entry => {
        bySiteId.set(entry.siteId, [
            ...(bySiteId.get(entry.siteId) ?? []),
            entry,
        ])
    })
    return [...bySiteId.entries()].map(([siteId, entries]) => {
        const modeCounts = sumModeCounts(entries)
        return {
            siteId,
            counts: sumSiteCounts(entries),
            mode: topModeOf(modeCounts),
            modeCounts,
        }
    })
}

type ModeCounts = WarehouseFlowSiteCounts["modeCounts"]

const sumModeCounts = (entries: WarehouseFlowSiteCounts[]): ModeCounts =>
    entries.reduce<ModeCounts>((sum, { modeCounts }) => {
        FLOW_MODE_KEYS.forEach(mode => {
            const count = modeCounts[mode]
            if (count) sum[mode] = (sum[mode] ?? 0) + count
        })
        return sum
    }, {})

const topModeOf = (modeCounts: ModeCounts): FlowModeKey | null =>
    FLOW_MODE_KEYS.reduce<FlowModeKey | null>(
        (best, mode) =>
            (modeCounts[mode] ?? 0) > (best ? (modeCounts[best] ?? 0) : 0)
                ? mode
                : best,
        null
    )

export type WarehouseFlowParty = WarehouseFlowView["counterparties"][number]

export type WarehouseFlowRow =
    | {
          key: string
          kind: "party"
          party: WarehouseFlowParty
          counts: WarehouseFlowCounts
          bySite: WarehouseFlowSiteCounts[]
      }
    | {
          key: string
          kind: "others"
          counterpartyCount: number
          counts: WarehouseFlowCounts
          bySite: WarehouseFlowSiteCounts[]
      }

const mainSiteIndexOf = ({
    party,
    siteOrder,
}: {
    party: WarehouseFlowParty
    siteOrder: Map<string, number>
}): number => {
    const busiest = [...party.bySite].sort(
        (a, b) =>
            totalOf(b.counts) - totalOf(a.counts) ||
            (siteOrder.get(a.siteId) ?? 0) - (siteOrder.get(b.siteId) ?? 0)
    )[0]
    return busiest ? (siteOrder.get(busiest.siteId) ?? 0) : siteOrder.size
}

export const buildFlowRows = ({
    view,
    maxRows,
}: {
    view: WarehouseFlowView
    maxRows: number
}): WarehouseFlowRow[] => {
    const siteOrder = new Map(view.sites.map((site, index) => [site.id, index]))
    const parties = view.counterparties
        .map((party, rank) => ({
            party,
            rank,
            siteIndex: mainSiteIndexOf({ party, siteOrder }),
        }))
        .sort((a, b) => a.siteIndex - b.siteIndex || a.rank - b.rank)
    const hasOthers = view.others.bySite.length > 0
    const isFolding = parties.length + (hasOthers ? 1 : 0) > maxRows
    const shownCount = isFolding ? Math.max(0, maxRows - 1) : parties.length
    const kept = [...parties]
        .sort((a, b) => a.rank - b.rank)
        .slice(0, shownCount)
        .map(({ party }) => party)
    const shown = parties.filter(({ party }) => kept.includes(party))
    const folded = parties.filter(({ party }) => !kept.includes(party))

    const rows: WarehouseFlowRow[] = shown.map(({ party, rank }) => ({
        key: `party-${rank}`,
        kind: "party",
        party,
        counts: party.counts,
        bySite: party.bySite,
    }))
    const othersBySite = mergeSiteCounts([
        view.others.bySite,
        ...folded.map(({ party }) => party.bySite),
    ])
    if (othersBySite.length > 0) {
        rows.push({
            key: "others",
            kind: "others",
            counterpartyCount: view.others.counterpartyCount + folded.length,
            counts: sumSiteCounts(othersBySite),
            bySite: othersBySite,
        })
    }
    return rows
}

const STAGE_PROGRESS = {
    new: 0.3,
    booked: 0.5,
    onHold: 0.6,
    inTransit: 0.78,
} as const

export const connectorLane = ({
    counts,
    mode,
}: {
    counts: WarehouseFlowCounts
    mode: FlowModeKey | null
}): FlowConnectorLane => {
    const loadCount = openOf(counts)
    const weighted = OPEN_STAGES.reduce(
        (sum, stage) => sum + counts.stages[stage] * STAGE_PROGRESS[stage],
        0
    )
    return {
        mode,
        loadCount,
        progress: loadCount > 0 ? weighted / loadCount : 0,
        isBehind: behindOf(counts) > 0,
    }
}

export const modesOf = (view: WarehouseFlowView): FlowModeKey[] => {
    const present = new Set(
        [
            ...view.counterparties.flatMap(party => party.bySite),
            ...view.others.bySite,
        ].map(entry => entry.mode)
    )
    return FLOW_MODE_KEYS.filter(mode => present.has(mode))
}

export const isLargeLayout = (view: WarehouseFlowView): boolean =>
    view.sites.length <= 2

const OTHER_SITE_ID = "other"

export const foldSites = ({
    view,
    maxSites,
}: {
    view: WarehouseFlowView
    maxSites: number
}): WarehouseFlowView => {
    if (view.sites.length <= maxSites) return view
    const named = view.sites.filter(site => site.kind === "site")
    const other = view.sites.find(site => site.kind === "other")
    const none = view.sites.filter(site => site.kind === "none")
    const keepCount = Math.max(1, maxSites - none.length - 1)
    const folded = named.slice(keepCount)
    if (folded.length === 0) return view
    const foldedIds = new Set(folded.map(site => site.id))
    const remap = (bySite: WarehouseFlowSiteCounts[]) =>
        bySite.some(entry => foldedIds.has(entry.siteId))
            ? mergeSiteCounts([
                  bySite.map(entry =>
                      foldedIds.has(entry.siteId)
                          ? { ...entry, siteId: OTHER_SITE_ID }
                          : entry
                  ),
              ])
            : bySite
    const mergedOther: WarehouseFlowSite = {
        id: OTHER_SITE_ID,
        kind: "other",
        place: null,
        placeCount: (other?.placeCount ?? 0) + folded.length,
        counts: sumSiteCounts([...(other ? [other] : []), ...folded]),
    }
    return {
        ...view,
        sites: [...named.slice(0, keepCount), mergedOther, ...none],
        counterparties: view.counterparties.map(party => ({
            ...party,
            bySite: remap(party.bySite),
        })),
        others: { ...view.others, bySite: remap(view.others.bySite) },
    }
}
